"use client";

import { useState } from "react";
import { sanitize } from "@/lib/sanitize";
import { Button } from "@/components/ui/button";

const TRUNCATE_WORDS = 150;

// The same on server and client, so the read-more decision never differs
// between the server render and hydration.
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/&[a-z#0-9]+;/gi, " ").trim();
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

interface TruncatableDescriptionProps {
  html: string;
  readMoreLabel: string;
  readLessLabel: string;
  /** Show read more when plain text exceeds this many words */
  maxWords?: number;
  proseClassName?: string;
}

export function TruncatableDescription({
  html,
  readMoreLabel,
  readLessLabel,
  maxWords = TRUNCATE_WORDS,
  proseClassName =
    "prose prose-sm prose-muted max-w-none text-pretty [&>p]:my-3 [&>div]:my-3 [&_a]:text-blue-600 [&_a]:underline prose-strong:text-foreground",
}: TruncatableDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const plainText = stripHtml(html);
  const isLong = countWords(plainText) > maxWords;

  if (!html?.trim()) return null;

  const sanitized = sanitize(
    html.replace(/<p><br><\/p>/g, '<div class="h-2"></div>'),
  );

  if (!isLong) {
    return (
      <div
        className={proseClassName}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return (
    <div>
      {/* A mask, not an overlay, so the fade works on any background */}
      <div
        className={
          expanded
            ? undefined
            : "max-h-40 overflow-hidden [mask-image:linear-gradient(to_bottom,black_50%,transparent)]"
        }
      >
        <div
          className={proseClassName}
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      </div>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 mt-1.5 text-xs text-primary hover:text-primary/90"
        onClick={() => {
          if (expanded) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
          setExpanded(!expanded);
        }}
      >
        {expanded ? readLessLabel : readMoreLabel}
      </Button>
    </div>
  );
}
